import {useEffect, useState} from 'react';
import {Link} from 'react-router';
import type {TodayOperationsProjection} from '../../../../packages/reporting/reporting-projection.ts';
import type {SmtReportingPort} from '../smt-reporting-port.ts';

const money=(minor:number|null)=>minor===null?'—':`HK$${(minor/100).toFixed(2)}`;
const timeLabel=(value:string)=>{
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime())?value:parsed.toLocaleTimeString('zh-HK',{hour:'2-digit',minute:'2-digit'});
};

function Metric({label,value}:{label:string;value:string|number}){
  return <div className="report-metric"><small>{label}</small><strong>{value}</strong></div>;
}

function TodayReport({report}:{report:TodayOperationsProjection}){
  const products=report.productPerformance.ranking.slice(0,5);
  return <div className="report-grid">
    <section className="panel report-card report-core" aria-label="今日核心">
      <div className="report-card-head"><div><span className="eyebrow">A6 · LIVE</span><h2>今日核心</h2></div><span className="report-live">LIVE</span></div>
      <div className="report-metric-grid core-metrics">
        <Metric label="營業額" value={money(report.turnoverMinor)}/>
        <Metric label="訂單" value={report.orderCount}/>
        <Metric label="客單價" value={money(report.aovMinor)}/>
      </div>
      <div className="report-inline-facts"><span>實收 <b>{money(report.receivedMinor)}</b></span><span>Net Received <b>{money(report.netReceivedMinor)}</b></span></div>
    </section>

    <section className="panel report-card" aria-label="營運例外">
      <div className="report-card-head"><div><span className="eyebrow">Exceptions</span><h2>營運例外</h2></div></div>
      <div className="report-metric-grid">
        <Metric label="未完成訂單" value={report.incompleteOrderCount}/>
        <Metric label="堂食 Session" value={report.openDineIn.sessionCount}/>
        <Metric label="堂食 Exposure" value={money(report.openDineIn.exposureMinor)}/>
        <Metric label="退款" value={money(report.refundMinor)}/>
      </div>
      <div className="report-inline-facts"><span>Correction In <b>{money(report.correctionInMinor)}</b></span><span>Correction Out <b>{money(report.correctionOutMinor)}</b></span></div>
    </section>

    <section className="panel report-card" aria-label="收入結構">
      <div className="report-card-head"><div><span className="eyebrow">Structure</span><h2>收入結構</h2></div></div>
      <div className="report-split-list">
        <div><h3>訂單來源</h3>{report.orderBreakdown.length?report.orderBreakdown.map(row=><div className="report-row" key={`${row.source}:${row.channel}`}><span>{row.source} · {row.channel}</span><b>{row.orderCount}單 · {money(row.grossOrderMinor)}</b></div>):<p className="report-empty">暫無訂單來源</p>}</div>
        <div><h3>付款方式</h3>{report.paymentBreakdown.length?report.paymentBreakdown.map(row=><div className="report-row" key={row.method}><span>{row.method}</span><b>{row.orderCount}單 · {money(row.netMinor)}</b></div>):<p className="report-empty">暫無付款資料</p>}</div>
      </div>
    </section>

    <section className="panel report-card" aria-label="產品表現">
      <div className="report-card-head"><div><span className="eyebrow">Product</span><h2>產品表現</h2></div></div>
      {report.productPerformance.sourceSafety==='source_not_established'?<p className="report-notice">產品銷售來源尚未建立；B唔會用訂單資料自行推算排名。</p>:products.length?<div className="report-ranking">{products.map(row=><div className="report-rank-row" key={row.productId}><span className="report-rank">#{row.rank}</span><div><b>{row.customerName}</b><small>{row.operationsName}</small></div><strong>{row.quantity}件</strong><span>{money(row.lineAmountMinor)}</span></div>)}</div>:<p className="report-empty">今日未有產品排名</p>}
    </section>

    <section className="panel report-card" aria-label="現金位置">
      <div className="report-card-head"><div><span className="eyebrow">Cash</span><h2>現金位置</h2></div></div>
      <div className="report-metric-grid">
        <Metric label="開更現金" value={money(report.cashPosition.openingCashMinor)}/>
        <Metric label="Cash In" value={money(report.cashPosition.cashInMinor)}/>
        <Metric label="Cash Out" value={money(report.cashPosition.cashOutMinor)}/>
        <Metric label="現金付款淨額" value={money(report.cashPosition.cashPaymentNetMinor)}/>
        <Metric label="Expected Cash" value={money(report.cashPosition.expectedCashMinor)}/>
        <Metric label="已點算" value={money(report.cashPosition.countedCashMinor)}/>
      </div>
      <div className="report-inline-facts"><span>Variance <b>{money(report.cashPosition.varianceMinor)}</b></span></div>
    </section>

    <section className="panel report-card" aria-label="營運／更新">
      <div className="report-card-head"><div><span className="eyebrow">Freshness</span><h2>營運／更新</h2></div><span className="report-live">{report.mode}</span></div>
      <div className="report-status-list"><span>營業日 <b>{report.businessDate}</b></span><span>最後成功更新 <b>{timeLabel(report.lastSuccessfulRefreshAt)}</b></span><span>未分配訂單調整 <b>{money(report.unallocatedOrderAdjustmentMinor)}</b></span></div>
      <Link className="report-ops-link" to="/business-day">服務狀態／ETA／日結 → 今日營運</Link>
    </section>
  </div>;
}

export function ReportingPage({runtime}:{runtime:Pick<SmtReportingPort,'queryReporting'>}){
  const [refreshToken,setRefreshToken]=useState(0);
  const [result,setResult]=useState<Awaited<ReturnType<SmtReportingPort['queryReporting']>>|null>(null);
  const [loading,setLoading]=useState(true);
  const [failure,setFailure]=useState<string|null>(null);

  useEffect(()=>{
    let ignore=false;
    const load=async()=>{
      if(!ignore)setLoading(true);
      try{
        const next=await runtime.queryReporting({kind:'report.today'});
        if(!ignore){setResult(next);setFailure(null);}
      }catch{
        if(!ignore){setResult(null);setFailure('REPORTING_QUERY_FAILED');}
      }finally{
        if(!ignore)setLoading(false);
      }
    };
    void load();
    const timer=window.setInterval(()=>void load(),60_000);
    return()=>{ignore=true;window.clearInterval(timer);};
  },[runtime,refreshToken]);

  const rejected=result?.kind==='rejected'?result.code:null;
  const report=result?.kind==='accepted'&&result.queryKind==='report.today'?result.report:null;

  return <main className="report-page" aria-busy={loading}>
    <section className="panel report-hero">
      <div><span className="eyebrow">SMT · C/A6 Reporting</span><h1>報表</h1><p>只顯示 C Reporting Authority 回傳值；畫面唔自行重算營業額、客單價、現金或排名。</p></div>
      <button type="button" className="report-refresh" disabled={loading} onClick={()=>setRefreshToken(value=>value+1)}>重新整理</button>
    </section>
    {loading&&!result?<section className="panel report-state" role="status">正在讀取 C/A6 報表…</section>:null}
    {!loading&&(failure||rejected)?<section className="panel report-state report-error" role="alert"><b>未能載入今日報表</b><span>{failure??rejected}</span><small>畫面保持 fail-closed，唔會用本地資料補數。</small></section>:null}
    {!loading&&report?<TodayReport report={report}/>:null}
  </main>;
}
